// [INVENTORY-CSV-EXPORT 2026-07-03] Builds the "Download record" file for the
// Inventory / My Inventory tabs. Still zero dependencies.
//
// [XLSX 2026-08-18] The export is now a REAL .xlsx, not a CSV. A CSV carries no cell
// types, so Excel re-guessed every cell on open and destroyed two columns:
//   • Company Record ID (an 11-digit HubSpot id) was read as a number and shown as
//     5.75279E+10 — and saving the file wrote that shortened text back, losing the id.
//     The old comment here claimed the id "stays TEXT on purpose"; it did not. Not
//     calling Number() only changes the JS value, never the characters in the file.
//   • Month was date-coerced, worked around by hiding a U+00A0 inside the value.
// See utils/xlsx.ts. Every non-numeric cell is now written as t="inlineStr", which
// states the type outright, so Excel cannot reinterpret it — through any number of
// save/reopen cycles. The U+00A0 hack is therefore gone, and a plain formula like
// =A2="Jun 2026" matches again.
//
// [CSV-COLUMN-PICKER 2026-07-28] The column set is NOT hard-coded here. The caller
// passes the columns to export — the page's OWN grid columns, filtered by the modal's
// column picker — so the file can carry EVERY column the table shows and Inventory /
// My Inventory each export their own set without this file knowing which page asked.

import { InventoryRow } from '../types';
import { buildXlsx, XlsxCell } from './xlsx';

/* [PERF 2026-08-14] Built ONCE — `toLocaleDateString(locale, {options})` constructs a fresh
 * Intl.DateTimeFormat per call (measured 90us on V8 vs 1.5us cached), and this runs once per
 * EXPORTED ROW. */
const MONTH_SHORT_YEAR_FMT = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' });

// [MONTH-CELL 2026-08-13] Short month name + 4-digit year — "Jun 2026", "Oct 2024".
// `row.month` arrives as 'YYYY-MM-DD…', so slice to the 'YYYY-MM' the formatter expects.
//
// [XLSX 2026-08-18] This used to separate month and year with a U+00A0, because a CSV let
// Excel date-parse "Nov 2024" into 1-Nov-2024, render it "Nov-24", and — on save — write
// that display text back, so a 2024 row reopened as 24-Nov-2026. U+00A0 was the one
// separator Excel date parsing rejected. The cell is now written as an explicit xlsx inline
// string, so Excel is TOLD it is text and never parses it at all. A normal space is
// therefore safe again, which also restores plain formula matching (=A2="Jun 2026").
function formatMonthCell(month: string | null): string {
  const ym = String(month || '').slice(0, 7);
  const [y, m] = ym.split('-');
  if (!y || !m) return ym;
  return MONTH_SHORT_YEAR_FMT.format(new Date(parseInt(y, 10), parseInt(m, 10) - 1));
}

/** One exported column: the grid's field key + the header text to print. */
export interface ExportColumn {
  key: string;
  label: string;
}

// Columns emitted as REAL numbers so Excel can sum/pivot them (everything else is
// text). Mirrors the grid's numeric columns, including the five computed ones
// (Sales Pending Payment, At-Risk Expected, Unmapped Payments, Pending Payment,
// Delayed Days) whose values arrive via the caller's `valueOf` resolver.
//
// company_record_id is deliberately ABSENT from this set, so it is written as a text
// cell. That is what actually keeps an 11-digit HubSpot id out of scientific notation
// — the xlsx cell type, not the JS type. Do not add it here.
const NUMERIC_KEYS = new Set([
  'amount', 'billing_date', 'num_campaigns', 'total_payment_received', 'refunded_disputed',
  'recurring_retention', 'recurring_so', 'one_time_so', 'sales_pending_payment', 'recovery',
  'discount', 'at_risk_expected_payment', 'pia_amount', 'pia_month', 'delayed_days',
  'unmapped_payments', 'pending_good', 'renewal_amount', 'sales_pending_recurring',
  'sales_pending_one_time',
]);

// One cell: Month prints as "Jul 2026" (see formatMonthCell), numeric columns are
// coerced to numbers (a non-numeric value falls back to its text), everything else
// prints as text. Blank/null → empty cell.
function cellValue(
  row: InventoryRow,
  key: string,
  valueOf: (row: InventoryRow, key: string) => unknown,
): string | number {
  const v = valueOf(row, key);
  if (key === 'month') return formatMonthCell(v == null ? '' : String(v));
  if (v == null || v === '') return '';
  if (NUMERIC_KEYS.has(key)) {
    const n = Number(v);
    return Number.isFinite(n) ? n : String(v);
  }
  return String(v);
}

/** Serialise inventory rows to .xlsx bytes.
 *  `cols` = the chosen columns, in the table's own order (the caller filters the
 *  grid's column list by the picker's selection, so click order never reshuffles
 *  the file). `valueOf` resolves a row's value for a key — the page passes its
 *  `sortValue`, so COMPUTED columns (Sales Pending Payment, At-Risk Expected,
 *  Unmapped Payments, Pending Payment, Delayed Days) export the same number the
 *  grid shows rather than an empty field; it defaults to the raw row field.
 *  Rows are sorted by Month ascending (oldest first), then Website A–Z within a
 *  month — the server returns them website-ordered with months interleaved, which
 *  reads jumbled in Excel when several months are exported.
 *
 *  [XLSX 2026-08-18] The CSV formula-injection guard (prefixing a leading = + - @
 *  with an apostrophe) is gone because it is no longer needed: an xlsx inline string
 *  is data, not a formula — a cell only becomes one via an explicit <f> element,
 *  which this writer never emits. So untrusted websites/emails are inert either way,
 *  and the exported value now matches the grid exactly, apostrophe-free. */
export function buildInventoryXlsx(
  rows: InventoryRow[],
  cols: ExportColumn[],
  valueOf: (row: InventoryRow, key: string) => unknown = (row, key) => (row as any)[key],
): Uint8Array<ArrayBuffer> {
  const sorted = [...rows].sort((a, b) => {
    const am = String(a.month || '').slice(0, 7);
    const bm = String(b.month || '').slice(0, 7);
    if (am !== bm) return am < bm ? -1 : 1; // 'YYYY-MM' compares chronologically as text
    return String(a.website || '').localeCompare(String(b.website || ''), undefined, { sensitivity: 'base' });
  });
  const header = cols.map((c) => c.label);
  const body: XlsxCell[][] = sorted.map((r) => cols.map((c) => cellValue(r, c.key, valueOf)));
  return buildXlsx('Inventory', header, body);
}

/** File name for the export — always plain `Inventory Data.xlsx`.
 *  [FILENAME 2026-07-30] It used to encode the selection and the download date
 *  (`Inventory_7-months_2026-07-30.csv`). The trailing date was the day the file was
 *  saved, not a month, but it read as one — a 7-month export looked like a July file.
 *  The file's own Month column already states which months it covers, so the name
 *  carries nothing. Repeat downloads get the browser's own "(1)", "(2)" suffix. */
export function inventoryExportFilename(): string {
  return 'Inventory Data.xlsx';
}

export { downloadXlsx } from './xlsx';
